import { useState } from "react";
import {
  Table2, ImagePlus, Shapes, Link2, Link2Off, CalendarClock, Omega, SeparatorHorizontal, Minus, ListChecks, CalendarDays, PenLine, PanelTop, Hash, ChevronDown, Images, Signature, TextCursorInput, Image as ImageIcon, QrCode, Bookmark,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../../ui/dropdown-menu";
import { RB, Group } from "./parts";
import { exec, insertHTML, insertText, restore, tableHTML, insertBlock } from "../cmd";
import { FILL_LABELS } from "../../../lib/saved";
import { SIG_PLACEHOLDER, INITIALS_PLACEHOLDER } from "../../../lib/pages";

const SYMBOLS = "© ® ™ § ¶ † ‡ • · … – — ‘ ’ “ ” « » ° ± × ÷ ≈ ≠ ≤ ≥ ∞ √ ∑ π µ Ω α β γ δ λ σ € £ ¥ ¢ ₹ ¼ ½ ¾ ¹ ² ³ ← → ↑ ↓ ↔ ⇒ ✓ ✗ ★ ☆ ♥ ♦ ♣ ♠ ☐ ☑ ☎ ✉ ✂ ☀ ☁ ☂ ♪ ¿ ¡ ß æ ø å ç é ñ ü".split(" ");

const TablePicker = () => {
  const [hover, setHover] = useState([0, 0]);
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="rb rb-tall" data-testid="rb-insert-table" onMouseDown={(e) => e.preventDefault()}><Table2 className="h-5 w-5" strokeWidth={1.75} /><span>Table</span></button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()}>
        <div className="text-xs text-muted-foreground mb-2" data-testid="table-picker-label">{hover[0] ? `${hover[0]} × ${hover[1]} table` : "Insert table"}</div>
        <div className="grid grid-cols-8 gap-[3px]" onMouseLeave={() => setHover([0, 0])}>
          {Array.from({ length: 64 }, (_, i) => {
            const r = Math.floor(i / 8) + 1;
            const c = (i % 8) + 1;
            const on = r <= hover[0] && c <= hover[1];
            return (
              <button key={i} type="button" data-testid={`table-cell-${r}x${c}`} onMouseEnter={() => setHover([r, c])}
                onMouseDown={(e) => e.preventDefault()} onClick={() => { insertHTML(tableHTML(r, c)); setOpen(false); }}
                className={`h-4 w-4 border rounded-[2px] transition-colors ${on ? "bg-primary/25 border-primary" : "bg-background"}`} />
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const SymbolPicker = () => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" className="rb rb-tall" data-testid="rb-insert-symbol" onMouseDown={(e) => e.preventDefault()}><Omega className="h-5 w-5" strokeWidth={1.75} /><span>Symbol</span></button>
    </PopoverTrigger>
    <PopoverContent className="w-[320px] p-3" onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()}>
      <div className="text-xs text-muted-foreground mb-2">Special characters</div>
      <div className="grid grid-cols-10 gap-1">
        {SYMBOLS.map((s, i) => (
          <button key={i} type="button" data-testid={`symbol-${i}`} onMouseDown={(e) => e.preventDefault()} onClick={() => insertText(s)}
            className="h-7 rounded border bg-background hover:bg-primary/10 hover:border-primary text-sm">{s}</button>
        ))}
      </div>
    </PopoverContent>
  </Popover>
);

const DropMenu = ({ icon: Icon, label, testid, children }) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger asChild>
      <button type="button" className="rb rb-tall" data-testid={testid} onMouseDown={(e) => e.preventDefault()}>
        <Icon className="h-5 w-5" strokeWidth={1.75} /><span className="flex items-center">{label}<ChevronDown className="h-3 w-3 opacity-60" /></span>
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent onCloseAutoFocus={(e) => e.preventDefault()}>{children}</DropdownMenuContent>
  </DropdownMenu>
);

const dateFormats = () => {
  const d = new Date();
  return [
    d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }),
    d.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
    d.toLocaleDateString(),
    d.toISOString().slice(0, 10),
    d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
    d.toLocaleString(),
  ];
};

export { SIG_PLACEHOLDER } from "../../../lib/pages";

const FILL = FILL_LABELS;
const fill = (l) => `<span class="fill-field" data-label="${l}"></span>&nbsp;`;

export const InsertTab = ({ openDialog, settings, setSettings, focusHeader, insertLogo, insertDigital }) => {
  const pn = settings.pageNumbers;
  const setPn = (patch) => setSettings({ ...settings, pageNumbers: { ...pn, ...patch } });
  return (
    <>
      <Group label="Tables"><TablePicker /></Group>
      <Group label="Illustrations">
        <RB tall icon={ImagePlus} label="Picture" onClick={() => openDialog("image", "upload")} testid="rb-insert-image" />
        <RB tall icon={Images} label="Library" onClick={() => openDialog("image", "library")} testid="rb-image-library" />
        <RB tall icon={Shapes} label="Shapes" onClick={() => openDialog("image", "shapes")} testid="rb-insert-shapes" />
      </Group>
      <Group label="Links & text">
        <RB tall icon={Link2} label="Link" onClick={() => openDialog("link")} testid="rb-insert-link" />
        <RB tall icon={Link2Off} label="Unlink" onClick={() => exec("unlink")} testid="rb-unlink" />
        <DropMenu icon={CalendarClock} label="Date" testid="rb-insert-datetime">
          <DropdownMenuLabel className="text-xs">Insert date & time</DropdownMenuLabel>
          {dateFormats().map((f, i) => <DropdownMenuItem key={i} onSelect={() => { restore(); insertText(f); }} data-testid={`datetime-format-${i}`}>{f}</DropdownMenuItem>)}
        </DropMenu>
        <SymbolPicker />
      </Group>
      <Group label="Pages">
        <RB tall icon={SeparatorHorizontal} label="Break" onClick={() => insertHTML('<div class="page-break" contenteditable="false"></div><p><br></p>')} testid="rb-page-break" />
        <RB tall icon={Minus} label="Line" onClick={() => insertHTML("<hr><p><br></p>")} testid="rb-horizontal-line" />
      </Group>
      <Group label="Fields">
        <RB tall icon={ListChecks} label="Drop-down" onClick={() => openDialog("dropdown")} testid="rb-insert-dropdown" />
        <RB tall icon={CalendarDays} label="Date picker" testid="rb-insert-datepicker"
          onClick={() => insertHTML(`<span class="field-wrap" contenteditable="false"><input type="date" class="date-field" value="${new Date().toISOString().slice(0, 10)}"></span>&nbsp;`)} />
        <RB tall icon={PenLine} label="Signature" testid="rb-insert-signature"
          onClick={() => insertHTML(`<span class="sig-field" contenteditable="false">${SIG_PLACEHOLDER}</span>&nbsp;`)} />
        <RB tall icon={Signature} label="Initials" testid="rb-insert-initials"
          onClick={() => insertHTML(`<span class="sig-field sig-initials" data-kind="initials" contenteditable="false">${INITIALS_PLACEHOLDER}</span>&nbsp;`)} />
      </Group>
      <Group label="Fill-in">
        <DropMenu icon={TextCursorInput} label="Fields" testid="rb-fill-fields">
          <DropdownMenuItem onSelect={insertLogo} data-testid="field-logo"><ImageIcon className="h-4 w-4 mr-2" />Logo in header</DropdownMenuItem>
          <DropdownMenuSeparator />
          {FILL.map((l) => <DropdownMenuItem key={l} onSelect={() => insertHTML(fill(l))} data-testid={`field-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{l}</DropdownMenuItem>)}
          <DropdownMenuItem onSelect={() => insertHTML(`<p>${["Name / Company", "Address 1", "Address 2", "Postal code", "Country"].map(fill).join("<br>")}</p>`)} data-testid="field-address-block">Full address block</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => openDialog("savedfields")} data-testid="field-saved-fields"><Bookmark className="h-4 w-4 mr-2" />Saved fields & lists…</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => insertHTML('<span class="field-wrap" contenteditable="false"><input type="checkbox" class="chk-field"></span>&nbsp;')} data-testid="field-checkbox">Checkbox</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => insertBlock('<p class="doc-notice">This is a computer generated document. No signature required.</p><p><br></p>')} data-testid="field-computer-generated">“Computer generated — no signature required”</DropdownMenuItem>
          <DropdownMenuItem onSelect={insertDigital} data-testid="field-digitally-signed">“Digitally signed document” stamp</DropdownMenuItem>
        </DropMenu>
      </Group>
      <Group label="Payment">
        <RB tall icon={QrCode} label="Pay QR" onClick={() => openDialog("paylink")} testid="rb-insert-paylink" />
      </Group>
      <Group label="Header & footer">
        <RB tall icon={PanelTop} label="Header/Footer" onClick={focusHeader} testid="rb-header-footer" />
        <DropMenu icon={Hash} label="Page no." testid="rb-page-numbers">
          <DropdownMenuItem onSelect={() => setPn({ enabled: false })} data-testid="page-numbers-none">None</DropdownMenuItem>
          <DropdownMenuSeparator />
          {["top-left", "top-center", "top-right", "bottom-left", "bottom-center", "bottom-right"].map((p) => (
            <DropdownMenuItem key={p} onSelect={() => setPn({ enabled: true, position: p })} data-testid={`page-numbers-${p}`} className="capitalize">{p.replace("-", " · ")}{pn.enabled && pn.position === p ? " ✓" : ""}</DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs">Format</DropdownMenuLabel>
          {["{n}", "Page {n}", "Page {n} of {total}", "{n} / {total}"].map((f) => (
            <DropdownMenuItem key={f} onSelect={() => setPn({ enabled: true, format: f })} data-testid={`page-number-format-${f.replace(/[^a-z]/gi, "") || "n"}`}>{f.replace("{n}", "1").replace("{total}", "3")}{pn.format === f ? " ✓" : ""}</DropdownMenuItem>
          ))}
        </DropMenu>
      </Group>
    </>
  );
};
